import React, { useState, useCallback, useRef, useEffect } from 'react';
import QRCode from 'qrcode';
import './ImageSharing.css';

const TIMER_UPDATE_INTERVAL = 100;
const STORAGE_LIMIT_MESSAGE = 'Your request could NOT be completed. Try later....';

const getSharingApiBase = () => {
  if (process.env.REACT_APP_SHARING_API_BASE) {
    return process.env.REACT_APP_SHARING_API_BASE.replace(/\/$/, '');
  }
  // Always use the current domain. With the updated _redirects file,
  // /api/* paths are automatically routed to Functions handlers
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Fallback for SSR contexts
  return 'https://photremium.com';
};

const calculateTimeRemaining = (expiresAt) => {
  const remaining = expiresAt - Date.now();
  if (remaining <= 0) {
    return { expired: true, hours: 0, minutes: 0, seconds: 0, total: 0 };
  }
  const hours = Math.floor(remaining / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  return { expired: false, hours, minutes, seconds, total: remaining };
};

let sharedChannel = null;
try {
  sharedChannel = new BroadcastChannel('image-sharing-session');
} catch (e) {
  console.log('BroadcastChannel not available, session sync may be limited');
}

const ImageSharing = () => {
  const [mode, setMode] = useState('loading');
  const [sessionId, setSessionId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [receiverLink, setReceiverLink] = useState(null);
  const [files, setFiles] = useState([]);
  const [receiverFiles, setReceiverFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [uploadProgress, setUploadProgress] = useState({});
  const [totalProgress, setTotalProgress] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [currentlyUploading, setCurrentlyUploading] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState({ hours: 2, minutes: 0, seconds: 0 });
  const [showFilesDropdown, setShowFilesDropdown] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [copiedTarget, setCopiedTarget] = useState(null);
  const [showReloadModal, setShowReloadModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const uploadProgressRef = useRef({});
  const apiBase = getSharingApiBase();

  const fetchReceiverFiles = useCallback(async (sessId) => {
    try {
      const response = await fetch(`${apiBase}/api/sharing/session?action=get&sessionId=${sessId}`);
      if (!response.ok) throw new Error('Failed to fetch session');
      const session = await response.json();
      setReceiverFiles(session.files || []);
      setExpiresAt(session.expiresAt);
      if (Date.now() > session.expiresAt && !sessionExpired) {
        setSessionExpired(true);
      }
    } catch (err) {
      console.error('Failed to fetch receiver files:', err);
    }
  }, [apiBase, sessionExpired]);

  const createSession = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/sharing/session?action=create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerDevice: 'web' })
      });
      if (!response.ok) throw new Error(`Create session failed (${response.status})`);
      const data = await response.json();
      setSessionId(data.sessionId);
      setExpiresAt(data.expiresAt);

      const qrDataUrl = await QRCode.toDataURL(data.sessionId, {
        errorCorrectionLevel: 'H', type: 'image/png', width: 300, margin: 1
      });
      setQrCode(qrDataUrl);
      const baseUrl = window.location.origin;
      setReceiverLink(`${baseUrl}/image-sharing?join=${data.sessionId}`);
      setMode('host');
    } catch (err) {
      console.error('Session creation failed:', err);
      alert('Failed to create session');
    } finally {
      setIsLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (!expiresAt) return;
    const updateTimer = () => {
      const time = calculateTimeRemaining(expiresAt);
      setTimeRemaining({ hours: time.hours, minutes: time.minutes, seconds: time.seconds });
      if (time.expired && !sessionExpired) {
        setSessionExpired(true);
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      }
    };
    updateTimer();
    timerIntervalRef.current = setInterval(updateTimer, TIMER_UPDATE_INTERVAL);
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [expiresAt, sessionExpired]);

  useEffect(() => {
    if (sessionId && sharedChannel) {
      sharedChannel.postMessage({ type: 'SESSION_CREATED', sessionId, expiresAt, qrCode, receiverLink, mode });
    }
  }, [sessionId, expiresAt, qrCode, receiverLink, mode]);

  useEffect(() => {
    if (!sharedChannel) return;
    const handleMessage = (msg) => {
      if (msg.data.type === 'SESSION_CREATED' && !sessionId) {
        setSessionId(msg.data.sessionId);
        setExpiresAt(msg.data.expiresAt);
        setQrCode(msg.data.qrCode);
        setReceiverLink(msg.data.receiverLink);
        setMode(msg.data.mode);
      }
    };
    sharedChannel.addEventListener('message', handleMessage);
    return () => sharedChannel.removeEventListener('message', handleMessage);
  }, [sessionId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const joinSessionId = params.get('join');
    if (joinSessionId && !sessionId) {
      setSessionId(joinSessionId);
      setMode('receiver');
    }
  }, [sessionId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const joinSessionId = params.get('join');
    if (!sessionId && !isLoading && !joinSessionId) {
      createSession();
    }
  }, [createSession, isLoading, sessionId]);

  useEffect(() => {
    if (mode !== 'receiver' || !sessionId) return;
    const fetchInterval = setInterval(() => {
      fetchReceiverFiles(sessionId);
    }, 2000);
    fetchReceiverFiles(sessionId);
    return () => clearInterval(fetchInterval);
  }, [sessionId, mode, fetchReceiverFiles]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'r' || e.key === 'F5') {
        e.preventDefault();
        setShowReloadModal(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const showStorageLimitError = useCallback(() => { alert(STORAGE_LIMIT_MESSAGE); }, []);

  const requestUploadUrl = useCallback(async (file) => {
    // In production deployed site, avoid presigned R2 PUTs (CORS/signing issues).
    // Use backend upload endpoint directly so the server writes to R2.
    try {
      const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      if (!isLocal) {
        const uploadUrl = `${apiBase}/api/sharing/upload?sessionId=${encodeURIComponent(sessionId)}&fileName=${encodeURIComponent(file.name)}&filePath=${encodeURIComponent(file.webkitRelativePath || file.name)}`;
        return { uploadUrl, objectKey: `${sessionId}/${encodeURIComponent(file.webkitRelativePath || file.name)}`, contentType: file.type || 'application/octet-stream' };
      }

      // Fallback for local/dev: request presigned URL from server
      const response = await fetch(`${apiBase}/api/sharing/files?action=upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId, fileName: file.name, filePath: file.webkitRelativePath || file.name,
          fileSize: file.size, contentType: file.type || 'application/octet-stream'
        })
      });
      let payload = {};
      try { payload = await response.json(); } catch (err) { payload = {}; }
      if (response.status === 413) {
        showStorageLimitError();
        return { limitReached: true, error: STORAGE_LIMIT_MESSAGE };
      }
      if (!response.ok) throw new Error(payload.error || `Upload preparation failed (${response.status})`);
      return { uploadUrl: payload.uploadUrl, objectKey: payload.objectKey, contentType: payload.contentType || file.type || 'application/octet-stream' };
    } catch (err) {
      throw err;
    }
  }, [apiBase, sessionId, showStorageLimitError]);

  const markUploadComplete = useCallback(async (objectKey) => {
    const response = await fetch(`${apiBase}/api/sharing/files?action=complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, objectKey })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMsg = errorData.error || `Upload verification failed (${response.status})`;
      throw new Error(errorMsg);
    }
    const data = await response.json();
    return data;
  }, [apiBase, sessionId]);

  const abortUpload = useCallback(async (objectKey) => {
    if (!objectKey) return;
    await fetch(`${apiBase}/api/sharing/files?action=abort`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, objectKey })
    });
  }, [apiBase, sessionId]);

  const uploadFileToBackend = useCallback((file, contentType, onProgress) => {
    // Use multipart/form-data (FormData) to avoid CORS preflight for non-simple content-types
    return new Promise((resolve, reject) => {
      const url = `${apiBase}/api/sharing/upload`;
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('sessionId', sessionId);
      formData.append('fileName', file.name);
      formData.append('filePath', file.webkitRelativePath || file.name);
      formData.append('contentType', contentType || file.type || 'application/octet-stream');
      formData.append('file', file);

      xhr.open('POST', url);
      xhr.timeout = 300000;

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded, event.total);
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText || '{}');
            resolve(response);
          } catch (e) {
            resolve({ success: true, objectKey: file.name });
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText} ${xhr.responseText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload: Network error'));
      xhr.ontimeout = () => reject(new Error('Upload: Timeout'));

      xhr.send(formData);
    });
  }, [apiBase, sessionId]);

  const uploadFiles = useCallback(async (filesToUpload) => {
    if (!sessionId) return alert('No active session');
    const totalSize = filesToUpload.reduce((sum, file) => sum + file.size, 0);
    uploadProgressRef.current = {};
    setTotalBytes(totalSize);
    setUploadedBytes(0);
    setTotalProgress(0);

    for (const file of filesToUpload) {
      const fileId = `${file.name}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
      setCurrentlyUploading(file.name);
      setUploadProgress((prev) => ({
        ...prev, [fileId]: { name: file.name, bytes: 0, total: file.size, percent: 0 }
      }));

      try {
        // Upload file directly to backend (which stores in R2)
        const uploadResp = await uploadFileToBackend(
          file,
          file.type || 'application/octet-stream',
          (loaded) => {
            const safeLoaded = Math.min(loaded, file.size);
            setUploadProgress((prev) => ({
              ...prev, [fileId]: { ...prev[fileId], bytes: safeLoaded, percent: Math.round((safeLoaded / file.size) * 100) }
            }));
            setUploadedBytes((prevTotal) => {
              const previousBytes = uploadProgressRef.current[fileId]?.bytes || 0;
              const delta = Math.max(0, safeLoaded - previousBytes);
              uploadProgressRef.current[fileId] = { bytes: safeLoaded, total: file.size };
              const newTotal = prevTotal + delta;
              setTotalProgress(totalSize ? Math.min(99, Math.round((newTotal / totalSize) * 100)) : 0);
              return newTotal;
            });
          }
        );

        // Update progress
        setUploadedBytes((prevTotal) => {
          const previousBytes = uploadProgressRef.current[fileId]?.bytes || 0;
          const delta = Math.max(0, file.size - previousBytes);
          uploadProgressRef.current[fileId] = { bytes: file.size, total: file.size };
          const newTotal = prevTotal + delta;
          setTotalProgress(totalSize ? Math.min(100, Math.round((newTotal / totalSize) * 100)) : 0);
          return newTotal;
        });

        setFiles((prev) => [...prev, {
          id: fileId, name: file.name, size: file.size, status: 'complete',
          uploadedAt: new Date().toISOString(), objectKey: uploadResp.objectKey || file.name
        }]);
        setUploadProgress((prev) => ({ ...prev, [fileId]: { ...prev[fileId], percent: 100, complete: true } }));
      } catch (err) {
        console.error(`[UPLOAD_ERROR] Failed to upload ${file.name}:`, err.message);
        setUploadProgress((prev) => ({ ...prev, [fileId]: { ...prev[fileId], error: err.message } }));
        setFiles((prev) => [...prev, { id: fileId, name: file.name, size: file.size, status: 'error' }]);
      }
    }
    setCurrentlyUploading(null);
    setTotalProgress(100);
  }, [sessionId, uploadFileToBackend]);

  const handleFileSelect = useCallback((e) => {
    uploadFiles(Array.from(e.target.files || []));
  }, [uploadFiles]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const downloadFile = useCallback(async (objectKey, fileName) => {
    if (!sessionId) return;
    try {
      // Request download from backend endpoint (which retrieves from R2)
      const downloadUrl = `${apiBase}/api/sharing/download?sessionId=${encodeURIComponent(sessionId)}&objectKey=${encodeURIComponent(objectKey)}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      if (fileName) link.download = fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log(`[DOWNLOAD] Started download: ${fileName}`);
    } catch (err) {
      console.error(`[DOWNLOAD_ERROR] Failed to download ${fileName}:`, err);
      alert(`Failed to download ${fileName}: ${err.message}`);
    }
  }, [apiBase, sessionId]);

  const toggleFileSelection = useCallback((objectKey) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      newSet.has(objectKey) ? newSet.delete(objectKey) : newSet.add(objectKey);
      return newSet;
    });
  }, []);

  const selectAllFiles = useCallback(() => {
    const selectable = receiverFiles.filter((f) => f.status === 'complete');
    setSelectedFiles(selectedFiles.size === selectable.length ? new Set() : new Set(selectable.map((f) => f.objectKey)));
  }, [receiverFiles, selectedFiles]);

  const downloadSelectedFiles = useCallback(async () => {
    const selected = receiverFiles.filter((f) => selectedFiles.has(f.objectKey));
    if (!selected.length) return alert('Please select files to download');
    setIsDownloadingAll(true);
    try {
      for (const file of selected) {
        await downloadFile(file.objectKey, file.name);
        await new Promise((r) => setTimeout(r, 200));
      }
    } catch (err) { alert('Failed to download files'); } finally { setIsDownloadingAll(false); }
  }, [selectedFiles, receiverFiles, downloadFile]);

  const downloadAllFiles = useCallback(async () => {
    if (!receiverFiles.length) return alert('No files available');
    setIsDownloadingAll(true);
    try {
      for (const file of receiverFiles) {
        if (file.status === 'complete') {
          await downloadFile(file.objectKey, file.name);
          await new Promise((r) => setTimeout(r, 200));
        }
      }
    } catch (err) { alert('Failed to download files'); } finally { setIsDownloadingAll(false); }
  }, [receiverFiles, downloadFile]);

  const copyToClipboard = useCallback((text, target) => {
    navigator.clipboard.writeText(text);
    setCopiedTarget(target);
    setTimeout(() => setCopiedTarget(null), 2000);
  }, []);

  const endSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      await fetch(`${apiBase}/api/sharing/session?action=end`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId })
      });
      setSessionId(null); setQrCode(null); setReceiverLink(null); setFiles([]); setReceiverFiles([]);
      setSelectedFiles(new Set()); setUploadProgress({}); uploadProgressRef.current = {};
      setTotalBytes(0); setUploadedBytes(0); setTotalProgress(0); setSessionExpired(false);
      setMode('loading');
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    } catch (err) {}
  }, [apiBase, sessionId]);

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatTime = (minutes, seconds) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h > 0 ? h + 'hr : ' : ''}${m}m : ${seconds}s`;
  };

  const TopRightBadges = () => (
    <div className="top-right-badges">
      <div className="badge session-badge">
        <i className="fa-solid fa-fingerprint"></i> ID: {sessionId}
        <button className="badge-copy-btn" onClick={() => copyToClipboard(sessionId, 'id')}>
          {copiedTarget === 'id' ? <i className="fa-solid fa-check copy-tick"></i> : <i className="fa-regular fa-copy"></i>}
        </button>
      </div>
      <div className="badge timer-badge">
        <i className="fa-solid fa-clock"></i> {formatTime(timeRemaining.minutes, timeRemaining.seconds)}
      </div>
      {sessionExpired && <div className="badge expired-badge"><i className="fa-solid fa-triangle-exclamation"></i> Expired</div>}
    </div>
  );

  return (
    <>
      {showReloadModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal-card">
            <i className="fa-solid fa-circle-exclamation modal-icon"></i>
            <h3>Warning</h3>
            <p>Reloading the page will end the current session and even receiver could not download the files. Are you sure to reload?</p>
            <div className="modal-actions">
              <button className="btn-modal-yes" onClick={() => window.location.reload()}>Yes, Reload</button>
              <button className="btn-modal-no" onClick={() => setShowReloadModal(false)}>No, Cancel</button>
            </div>
          </div>
        </div>
      )}

      {mode === 'receiver' ? (
        <div className="image-sharing-container receiver-mode">
          <div className="receiver-session advanced-card">
            <TopRightBadges />
            <div className="receiver-header">
              <h1 className="receiver-title"><i className="fa-solid fa-download"></i> Receive Files</h1>
            </div>

            <div className="receiver-overview">
              <div className="overview-card">
                <div className="overview-icon"><i className="fa-solid fa-boxes-stacked"></i></div>
                <div className="overview-content">
                  <div className="overview-label">Total Files</div>
                  <div className="overview-value">{receiverFiles.length}</div>
                </div>
              </div>
              <div className="overview-card">
                <div className="overview-icon"><i className="fa-solid fa-hard-drive"></i></div>
                <div className="overview-content">
                  <div className="overview-label">Total Size</div>
                  <div className="overview-value">{formatSize(receiverFiles.reduce((sum, file) => sum + (file.size || 0), 0))}</div>
                </div>
              </div>
              <div className="overview-card">
                <div className="overview-icon"><i className="fa-solid fa-circle-check"></i></div>
                <div className="overview-content">
                  <div className="overview-label">Complete Files</div>
                  <div className="overview-value">{receiverFiles.filter((f) => f.status === 'complete').length}</div>
                </div>
              </div>
            </div>

            <div className="receiver-files-section">
              <div className="files-section-header">
                <h2><i className="fa-solid fa-folder-open"></i> Available Files</h2>
                <div className="files-actions">
                  {receiverFiles.length > 0 && (
                    <>
                      <button className="btn-select-all" onClick={selectAllFiles} disabled={isDownloadingAll}>
                        <i className={selectedFiles.size === receiverFiles.filter(f => f.status === 'complete').length && receiverFiles.length > 0 ? "fa-solid fa-square-check" : "fa-regular fa-square"}></i>
                        {selectedFiles.size === receiverFiles.filter(f => f.status === 'complete').length && receiverFiles.length > 0 ? ' Deselect All' : ' Select All'}
                      </button>
                      <button className="btn-download-all" onClick={downloadAllFiles} disabled={isDownloadingAll || receiverFiles.filter(f => f.status === 'complete').length === 0}>
                        <i className="fa-solid fa-cloud-arrow-down"></i> {isDownloadingAll ? 'Downloading...' : 'Download All'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {receiverFiles.filter(f => f.status === 'complete').length === 0 ? (
                <div className="no-files-message">
                  <div className="no-files-icon"><i className="fa-solid fa-hourglass-empty"></i></div>
                  <p>Waiting for files...</p>
                </div>
              ) : (
                <div className="receiver-files-list">
                  {receiverFiles.map((file, index) => (
                    file.status === 'complete' && (
                      <div key={file.objectKey || file.name} className="receiver-file-item">
                        <div className="file-checkbox">
                          <input type="checkbox" checked={selectedFiles.has(file.objectKey)} onChange={() => toggleFileSelection(file.objectKey)} id={`file-${index}`} />
                          <label htmlFor={`file-${index}`}></label>
                        </div>
                        <div className="file-details">
                          <div className="file-name">{file.name}</div>
                          <div className="file-size">{formatSize(file.size)}</div>
                        </div>
                        <button className="btn-download-single" onClick={() => downloadFile(file.objectKey, file.name)} disabled={isDownloadingAll} title="Download this file">
                          <i className="fa-solid fa-download"></i>
                        </button>
                      </div>
                    )
                  ))}
                </div>
              )}

              {selectedFiles.size > 0 && (
                <div className="selected-actions">
                  <button className="btn-download-selected" onClick={downloadSelectedFiles} disabled={isDownloadingAll}>
                    <i className="fa-solid fa-cloud-arrow-down"></i> Download {selectedFiles.size} Selected File{selectedFiles.size > 1 ? 's' : ''}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="image-sharing-container">
          <div className="sharing-session advanced-card">
            <TopRightBadges />

            <div className="session-main-grid">
              <div className="qr-column">
                <div className="qr-card">
                  {isLoading ? (
                    <div className="qr-code skeleton" style={{ width: '220px', height: '220px' }}></div>
                  ) : (
                    <>
                      {qrCode && <img src={qrCode} alt="Session QR Code" className="qr-code" />}
                      <p className="qr-hint"><i className="fa-solid fa-qrcode"></i> Scan to Join</p>
                    </>
                  )}
                </div>
                {receiverLink && !isLoading && (
                  <button className="btn-copy-link" onClick={() => copyToClipboard(receiverLink, 'link')}>
                    {copiedTarget === 'link' ? <i className="fa-solid fa-check copy-tick"></i> : <i className="fa-solid fa-link"></i>}
                    {copiedTarget === 'link' ? 'Copied!' : 'Copy Link'}
                  </button>
                )}
              </div>

              <div className="dropzone-column">
                <div className={`fb-dropzone ${dragOver ? 'fb-dropzone--active' : ''}`} onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }} onDrop={onDrop} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}>
                  <div className="fb-dropzone__cloud">
                    <i className="fa-solid fa-cloud-arrow-up"></i>
                  </div>
                  <h3>Drop your files here</h3>
                  <p>Or <span className="fb-dropzone__browse" onClick={() => fileInputRef.current?.click()}>browse files</span> from your device</p>
                  <p className="fb-dropzone__hint">
                    <i className="fa-regular fa-keyboard"></i> You can also paste <kbd>Ctrl</kbd> + <kbd>V</kbd>
                  </p>
                  <div style={{ marginTop: 20, display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <button className="fb-dropzone__btn" onClick={() => fileInputRef.current?.click()} style={{ marginTop: 0 }}>
                      <i className="fa-solid fa-folder-open"></i> Choose Files
                    </button>
                  </div>
                  <input ref={fileInputRef} type="file" accept="/*" multiple hidden onChange={(e) => { handleFileSelect(e); e.target.value = ''; }} />
                </div>
              </div>
            </div>

            {(totalBytes > 0 || Object.keys(uploadProgress).length > 0) && (
              <div className="advanced-stats">
                <div className="stat-card advanced">
                  <div className="stat-header">
                    <div className="stat-icon"><i className="fa-solid fa-boxes-stacked"></i></div>
                    <span className="stat-title">Total Files</span>
                  </div>
                  <div className="stat-body">
                    <div className="big-number">{files.length}</div>
                    <div className="stat-subtext">files selected</div>
                  </div>
                </div>
                <div className="stat-card advanced">
                  <div className="stat-header">
                    <div className="stat-icon"><i className="fa-solid fa-hard-drive"></i></div>
                    <span className="stat-title">Total Size</span>
                  </div>
                  <div className="stat-body">
                    <div className="big-number">{formatSize(totalBytes)}</div>
                    <div className="stat-subtext">total upload size</div>
                  </div>
                </div>
                <div className="stat-card advanced">
                  <div className="stat-header">
                    <div className="stat-icon"><i className="fa-solid fa-arrow-up-right-dots"></i></div>
                    <span className="stat-title">Uploaded</span>
                  </div>
                  <div className="stat-body">
                    <div className="big-number">{formatSize(uploadedBytes)}</div>
                    <div className="stat-subtext">uploaded so far</div>
                  </div>
                </div>
              </div>
            )}

            {Object.keys(uploadProgress).length > 0 && (
              <div className="progress-section">
                <div className="progress-info">
                  <div className="progress-label">Overall Progress: <strong>{totalProgress}%</strong></div>
                  {currentlyUploading && <div className="uploading-status">Uploading: {currentlyUploading}</div>}
                </div>
                <div className="progress-bar-container">
                  <div className="progress-bar large"><div className="progress-fill" style={{ width: `${totalProgress}%` }} /></div>
                </div>
                {Object.keys(uploadProgress).length > 0 && (
                  <div className="files-details-section">
                    <button className="btn-files-list" onClick={() => setShowFilesDropdown(!showFilesDropdown)}>
                      <i className="fa-solid fa-list"></i> Files ({Object.keys(uploadProgress).length})
                    </button>
                    {showFilesDropdown && (
                      <div className="files-list-detailed">
                        {Object.entries(uploadProgress).map(([id, prog]) => (
                          <div key={id} className="file-detail-item">
                            <div className="file-detail-header">
                              <span className="file-detail-name">{prog.name}</span>
                              <span className="file-detail-percent">{prog.percent || 0}%</span>
                            </div>
                            <div className="file-detail-progress">
                              <div className="progress-bar small"><div className="progress-fill" style={{ width: `${prog.percent || 0}%` }} /></div>
                              <span className="file-detail-size">{formatSize(prog.bytes || 0)} / {formatSize(prog.total || 0)}</span>
                            </div>
                            {prog.error && <div className="file-error" title={prog.error}><i className="fa-solid fa-triangle-exclamation"></i> {prog.error}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {files.length > 0 && (
              <div className="completed-files-section">
                <h3><i className="fa-solid fa-circle-check" style={{color: '#22c55e'}}></i> Completed Uploads ({files.length})</h3>
                <div className="completed-files-list">
                  {files.map((file) => (
                    <div key={file.id} className={`completed-file-item ${file.status}`}>
                      <div className="file-icon"><i className="fa-regular fa-file-image"></i></div>
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{formatSize(file.size)}</span>
                      </div>
                      <div className="file-status">
                        {file.status === 'complete' ? <i className="fa-solid fa-check"></i> : <i className="fa-solid fa-xmark"></i>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="session-actions">
              <button onClick={endSession} className="btn-end-session"><i className="fa-solid fa-power-off"></i> End Session</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageSharing;